// React Query
import {
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
// Http Methods
import { getRequest as get } from '../utils/HttpMethods';

import queryString from 'api/utils/queryStringBuilder';

import {
  ActionResponse,
  ActionRequest,
  isActionResponse,
  isActionRequest,
} from 'interfaces';
import {
  useListQuery,
  createEntity,
  useMutationQuery,
  updateEntity,
} from 'api/utils/queries';

export const actionManagementQueryKeys = {
  all: ['actionManagement'],
  getActionResponsesByClaim: (claimID: string) => [
    'actionManagement',
    'actionResponses',
    claimID,
  ],
  getActionResponsesByUser: (userID: string) => [
    'actionManagement',
    'actionResponses',
    'user',
    userID,
  ],
  getActionRequestsByClaim: (claimID: string) => [
    'actionManagement',
    'actionRequests',
    claimID,
  ],
  getActionRequestsByUser: (userID: string) => [
    'actionManagement',
    'actionRequests',
    'user',
    userID,
  ],
  getActionRequestsCount: (userID: string, siteID: string) => [
    'actionManagement',
    'actionRequests',
    'count',
    userID,
    siteID,
  ],
};

const getActionRequestsCount = async ({
  userID,
  siteID,
}: {
  userID: string;
  siteID: string;
}): Promise<number> =>
  await get<number>(
    'QBilling',
    `/resource/actionManagement/request/count${queryString({ userID, siteID })}`
  );

export const useGetActionRequestsCount = ({
  userID,
  siteID,
}: {
  userID: string;
  siteID: string;
}): UseQueryResult<number, Error> =>
  useQuery<number, Error>({
    queryKey: actionManagementQueryKeys.getActionRequestsCount(userID, siteID),
    queryFn: () => getActionRequestsCount({ userID, siteID }),
    enabled: Boolean(userID && siteID),
    staleTime: 60_000,
    retry: false,
  });

const getActionResponsesByClaim = async (
  claimID: string
): Promise<ActionResponse[]> =>
  await get<ActionResponse[]>(
    'QBilling',
    `/resource/actionManagement/response/claim${queryString({ claimID })}`
  );

export const useGetActionResponsesByClaim = ({
  claimID,
  displayNotification = false,
}: {
  claimID: string;
  displayNotification?: boolean;
}): UseQueryResult<ActionResponse[], Error> => {
  return useListQuery<ActionResponse>({
    queryKey: actionManagementQueryKeys.getActionResponsesByClaim(claimID),
    fetchFunction: getActionResponsesByClaim,
    params: claimID,
    enabled: !!claimID,
    isEntity: isActionResponse,
    displayNotification,
  });
};

const getActionRequestsByClaim = async (
  claimID: string
): Promise<ActionRequest[]> =>
  await get<ActionRequest[]>(
    'QBilling',
    `/resource/actionManagement/request/claim${queryString({ claimID })}`
  );

export const useGetActionRequestsByClaim = ({
  claimID,
  displayNotification = false,
}: {
  claimID: string;
  displayNotification?: boolean;
}): UseQueryResult<ActionRequest[], Error> => {
  return useListQuery<ActionRequest>({
    queryKey: actionManagementQueryKeys.getActionRequestsByClaim(claimID),
    fetchFunction: getActionRequestsByClaim,
    params: claimID,
    enabled: !!claimID,
    isEntity: isActionRequest,
    displayNotification,
  });
};

const getActionResponsesByUser = async (
  userID: string
): Promise<ActionResponse[]> =>
  await get<ActionResponse[]>(
    'QBilling',
    `/resource/actionManagement/response/user${queryString({ userID })}`
  );

export const useGetActionResponsesByUser = ({
  userID,
  displayNotification = false,
}: {
  userID: string;
  displayNotification?: boolean;
}): UseQueryResult<ActionResponse[], Error> => {
  return useListQuery<ActionResponse>({
    queryKey: actionManagementQueryKeys.getActionResponsesByUser(userID),
    fetchFunction: getActionResponsesByUser,
    params: userID,
    enabled: !!userID,
    isEntity: isActionResponse,
    displayNotification,
  });
};

const getActionRequestsByUser = async (
  userID: string
): Promise<ActionRequest[]> =>
  await get<ActionRequest[]>(
    'QBilling',
    `/resource/actionManagement/request/user${queryString({ userID })}`
  );

export const useGetActionRequestsByUser = ({
  userID,
  displayNotification = false,
}: {
  userID: string;
  displayNotification?: boolean;
}): UseQueryResult<ActionRequest[], Error> => {
  return useListQuery<ActionRequest>({
    queryKey: actionManagementQueryKeys.getActionRequestsByUser(userID),
    fetchFunction: getActionRequestsByUser,
    params: userID,
    enabled: !!userID,
    isEntity: isActionRequest,
    displayNotification,
  });
};

const createActionRequest = async (
  actionRequest: ActionRequest
): Promise<ActionRequest> =>
  createEntity<ActionRequest>({
    entity: actionRequest,
    functionName: 'createActionRequest',
    isEntity: isActionRequest,
    path: '/resource/actionManagement/request',
  });

export const useCreateActionRequest = (): UseMutationResult<
  ActionRequest,
  Error
> =>
  useMutationQuery<ActionRequest>({
    mutationFn: createActionRequest,
    successMessage: 'Action Request created successfully.',
    errorMessage: 'Failed to create Action Request.',
    queryKey: actionManagementQueryKeys.all,
  });

const createActionResponse = async (
  actionResponse: ActionResponse
): Promise<ActionResponse> =>
  createEntity<ActionResponse>({
    entity: actionResponse,
    functionName: 'createActionResponse',
    isEntity: isActionResponse,
    path: '/resource/actionManagement/response',
  });

export const useCreateActionResponse = (): UseMutationResult<
  ActionResponse,
  Error
> =>
  useMutationQuery<ActionResponse>({
    mutationFn: createActionResponse,
    successMessage: 'Action Response created successfully.',
    errorMessage: 'Failed to create Action Response.',
    queryKey: actionManagementQueryKeys.all,
  });

const updateActionRequest = async (
  actionRequest: ActionRequest
): Promise<ActionRequest> =>
  updateEntity<ActionRequest>({
    entity: actionRequest,
    functionName: 'updateActionRequest',
    isEntity: isActionRequest,
    path: '/resource/actionManagement/request',
  });

export const useUpdateActionRequest = (): UseMutationResult<
  ActionRequest,
  Error
> =>
  useMutationQuery<ActionRequest>({
    mutationFn: updateActionRequest,
    successMessage: 'Action Request updated successfully.',
    errorMessage: 'Failed to update Action Request.',
    queryKey: actionManagementQueryKeys.all,
  });

const updateActionResponse = async (
  actionResponse: ActionResponse
): Promise<ActionResponse> =>
  updateEntity<ActionResponse>({
    entity: actionResponse,
    functionName: 'updateActionResponse',
    isEntity: isActionResponse,
    path: '/resource/actionManagement/response',
  });
export const useUpdateActionResponse = (): UseMutationResult<
  ActionResponse,
  Error
> =>
  useMutationQuery<ActionResponse>({
    mutationFn: updateActionResponse,
    successMessage: 'Action Response updated successfully.',
    errorMessage: 'Failed to update Action Response.',
    queryKey: actionManagementQueryKeys.all,
  });
